import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '../../components/ui/Button';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { useCurrentOrg } from '../orgs/useCurrentOrg';
import { listAgentSessions, revokeAgentSession, type AgentSession } from './api';
import { SessionDetailDialog } from './SessionDetailDialog';

const DOCS_URL = 'https://developers.sigilkeys.com/agents/quickstart';

export function PoliciesSessionsPage() {
  const { org } = useCurrentOrg();
  const qc = useQueryClient();
  const orgId = org?.id ?? '';

  const sessions = useQuery({
    queryKey: ['agent-sessions', orgId],
    queryFn: () => listAgentSessions(orgId),
    enabled: !!orgId,
  });

  const [openDetail, setOpenDetail] = useState<AgentSession | null>(null);

  const revoke = useMutation({
    mutationFn: (id: string) => revokeAgentSession(orgId, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['agent-sessions', orgId] }),
  });

  const isTEEOrg = org?.signing_mode === 'tee';

  return (
    <div className="space-y-6">
      {!isTEEOrg && (
        <Card>
          <CardBody className="text-sm text-ink-secondary">
            Agent sessions are only available for organisations in <strong>TEE signing mode</strong>.
            Your organisation is currently configured for client-side signing.
          </CardBody>
        </Card>
      )}

      {isTEEOrg && (
        <Card>
          <CardHeader
            title="How it works"
            description="Sessions are created from your backend with a secret API key — never from the portal. The portal is where you watch what your agents are doing and revoke sessions that misbehave."
          />
          <CardBody className="space-y-4">
            <ol className="ml-5 list-decimal space-y-2 text-sm text-ink-secondary">
              <li>
                Issue a secret key (
                <Link to="/api-keys" className="font-medium text-bit2me hover:underline">
                  /api-keys
                </Link>
                ) in your backend. Treat it like any other secret.
              </li>
              <li>
                From your backend, <code className="rounded bg-surface-soft px-1 py-0.5 text-xs">POST /v1/s2s/agent-sessions</code> with the wallet, spend limits and expiry.
              </li>
              <li>
                Hand the returned <code className="rounded bg-surface-soft px-1 py-0.5 text-xs">as_live_…</code> token to your agent. It signs via{' '}
                <code className="rounded bg-surface-soft px-1 py-0.5 text-xs">SigilAgent</code> until policies stop it or you revoke.
              </li>
            </ol>

            <pre className="overflow-x-auto rounded-md border border-line bg-surface-soft/40 p-3 text-xs leading-relaxed">
{`curl -X POST https://api.sigilkeys.com/v1/s2s/agent-sessions \\
  -H "Authorization: Bearer sk_live_…" \\
  -H "Content-Type: application/json" \\
  -d '{
    "wallet_id": "WALLET_UUID",
    "name": "trading-bot-prod",
    "max_spend_per_tx_native": "100000000000000000",
    "max_spend_total_native": "1000000000000000000",
    "allowed_chains": ["base", "arbitrum"],
    "allowed_methods": ["signMessage", "sendTransaction"],
    "expires_at": "2026-12-31T23:59:59Z"
  }'`}
            </pre>

            <div className="flex flex-wrap gap-2">
              <a
                href={DOCS_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex h-9 items-center rounded-lg border border-line bg-surface-white px-3 text-sm font-medium text-ink-primary hover:border-bit2me hover:text-bit2me"
              >
                Full guide on developers.sigilkeys.com →
              </a>
              <Link
                to="/api-keys"
                className="inline-flex h-9 items-center rounded-lg border border-line bg-surface-white px-3 text-sm font-medium text-ink-primary hover:border-bit2me hover:text-bit2me"
              >
                Manage secret keys
              </Link>
            </div>
          </CardBody>
        </Card>
      )}

      <Card>
        <CardHeader title="Sessions" description="Read-only. Revoke from here; create from your backend." />
        <CardBody className="p-0">
          {sessions.isLoading && <div className="p-6 text-sm text-ink-tertiary">Loading…</div>}
          {sessions.data && sessions.data.length === 0 && (
            <div className="p-6 text-sm text-ink-tertiary">
              No agent sessions yet. Once your backend mints one with{' '}
              <code className="rounded bg-surface-soft px-1 py-0.5 text-xs">POST /v1/s2s/agent-sessions</code> it appears here.
            </div>
          )}
          {sessions.data && sessions.data.length > 0 && (
            <table className="w-full text-left text-sm">
              <thead className="border-b border-line-soft text-xs uppercase tracking-eyebrow text-ink-tertiary">
                <tr>
                  <th className="px-4 py-2 font-medium">Name</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 font-medium">Wallet</th>
                  <th className="px-4 py-2 font-medium">Spent / limit</th>
                  <th className="px-4 py-2 font-medium">Chains</th>
                  <th className="px-4 py-2 font-medium">Expires</th>
                  <th className="px-4 py-2 font-medium" />
                </tr>
              </thead>
              <tbody>
                {sessions.data.map((s) => (
                  <tr key={s.id} className="border-t border-line-soft hover:bg-surface-soft/40">
                    <td className="px-4 py-2 font-medium text-ink-primary">{s.name}</td>
                    <td className="px-4 py-2">
                      <StatusBadge status={s.status} />
                    </td>
                    <td className="px-4 py-2 font-mono text-xs text-ink-secondary">{shortAddr(s.wallet_address)}</td>
                    <td className="px-4 py-2 font-mono text-xs text-ink-secondary">
                      {weiToEth(s.spent_native)} /{' '}
                      {s.max_spend_total_native ? weiToEth(s.max_spend_total_native) : '∞'}
                    </td>
                    <td className="px-4 py-2 text-xs text-ink-secondary">
                      {s.allowed_chains.length > 0 ? s.allowed_chains.join(', ') : 'any'}
                    </td>
                    <td className="px-4 py-2 text-xs text-ink-secondary">{formatExpires(s.expires_at)}</td>
                    <td className="px-4 py-2 text-right">
                      <Button variant="ghost" onClick={() => setOpenDetail(s)}>
                        View
                      </Button>
                      {s.status === 'active' && (
                        <Button variant="ghost" onClick={() => revoke.mutate(s.id)}>
                          Revoke
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>

      {openDetail && (
        <SessionDetailDialog
          orgId={orgId}
          sessionId={openDetail.id}
          onClose={() => setOpenDetail(null)}
        />
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    active: 'bg-ok/10 text-ok',
    revoked: 'bg-danger/10 text-danger',
    expired: 'bg-surface-soft text-ink-tertiary',
  };
  return (
    <span className={`rounded px-1.5 py-0.5 font-mono text-[10px] uppercase ${map[status] ?? ''}`}>
      {status}
    </span>
  );
}

function weiToEth(wei: string): string {
  try {
    const n = BigInt(wei);
    const whole = n / 10n ** 18n;
    const frac = (n % 10n ** 18n).toString().padStart(18, '0').replace(/0+$/, '');
    return frac ? `${whole}.${frac}` : whole.toString();
  } catch {
    return wei;
  }
}

function formatExpires(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diff = d.getTime() - now.getTime();
  if (diff < 0) return 'expired';
  const days = Math.floor(diff / 86_400_000);
  if (days > 1) return `in ${days} days`;
  const hours = Math.floor(diff / 3_600_000);
  return `in ${hours}h`;
}

function shortAddr(addr: string | undefined): string {
  if (!addr) return '—';
  return addr.length > 16 ? `${addr.slice(0, 8)}…${addr.slice(-4)}` : addr;
}
